import {
  Button,
  Card,
  Col,
  Divider,
  List,
  Progress,
  Row,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import dayjs from "dayjs";
import type { TaskItem, MetricItem, SubtaskItem } from "../../lib/types";
import type { ProjectItem } from "./types";
import { getStatusColor } from "./types";
import { markdownComponents } from "../MarkdownLink";

interface OverviewTabProps {
  project: ProjectItem;
  tasks: TaskItem[];
  metrics: MetricItem[];
  subtasks: SubtaskItem[];
  summaryRefreshing: boolean;
  summaryPreview: string;
  onRefreshSummary: () => void;
  onDownloadSummary: () => void;
}

export default function OverviewTab({
  project,
  tasks,
  metrics,
  subtasks,
  summaryRefreshing,
  summaryPreview,
  onRefreshSummary,
  onDownloadSummary,
}: OverviewTabProps) {
  const totalSubtasks = subtasks.length;
  const completedSubtasks = subtasks.filter((subtask) => subtask.status === "done").length;
  const overallProgress = totalSubtasks
    ? Math.round((completedSubtasks / totalSubtasks) * 100)
    : 0;
  const pendingSubtasks = totalSubtasks - completedSubtasks;

  const upcomingWindowEnd = dayjs().add(14, "day");
  const upcomingSubtasks = subtasks.filter(
    (subtask) =>
      subtask.dueDate &&
      subtask.status !== "done" &&
      dayjs(subtask.dueDate).isBefore(upcomingWindowEnd, "day"),
  );
  const upcomingMetrics = metrics.filter(
    (metric) =>
      metric.dueDate &&
      !metric.completedAt &&
      dayjs(metric.dueDate).isBefore(upcomingWindowEnd, "day"),
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title="Project Overview"
        extra={
          <Button type="primary" onClick={onDownloadSummary}>
            Download Summary (PDF)
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Typography.Text type="secondary">Project Type</Typography.Text>
            <Typography.Title level={5} style={{ marginTop: 4 }}>
              {project.type?.replace(/-/g, " ") ?? "—"}
            </Typography.Title>
          </Col>
          <Col span={12}>
            <Typography.Text type="secondary">Current Status</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={getStatusColor(project.status)}>{project.status}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text type="secondary">Start Date</Typography.Text>
            <Typography.Title level={5} style={{ marginTop: 4 }}>
              {project.startDate
                ? new Date(project.startDate).toLocaleDateString()
                : "—"}
            </Typography.Title>
          </Col>
          <Col span={12}>
            <Typography.Text type="secondary">End Date</Typography.Text>
            <Typography.Title level={5} style={{ marginTop: 4 }}>
              {project.endDate
                ? new Date(project.endDate).toLocaleDateString()
                : "—"}
            </Typography.Title>
          </Col>
        </Row>
        <Divider />
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text type="secondary">Overall Progress</Typography.Text>
          <Progress
            percent={overallProgress}
            aria-label={`Overall task completion: ${overallProgress} percent`}
          />
          <Typography.Text type="secondary">
            {completedSubtasks} completed · {pendingSubtasks} pending
          </Typography.Text>
        </Space>
      </Card>
      <Card
        title="AI-Generated Summary"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefreshSummary}
            disabled={summaryRefreshing}
            aria-label="Refresh AI-generated summary"
          >
            {summaryRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        }
      >
        <Typography.Text type="secondary">
          {project.aiSummaryUpdatedAt
            ? `Last updated: ${new Date(project.aiSummaryUpdatedAt).toLocaleString()}`
            : "Summary auto-updates when changes are made"}
        </Typography.Text>
        <Divider />
        {summaryRefreshing ? (
          <div aria-live="polite">
            <Skeleton active paragraph={{ rows: 6 }} />
            {summaryPreview && (
              <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
                {summaryPreview}
              </Typography.Paragraph>
            )}
          </div>
        ) : project.aiSummary ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {project.aiSummary}
          </ReactMarkdown>
        ) : (
          <Space direction="vertical" size="middle">
            <Typography.Text type="secondary">
              No summary generated yet.
            </Typography.Text>
            <Button type="primary" onClick={onRefreshSummary}>
              Generate Initial Summary
            </Button>
          </Space>
        )}
      </Card>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic title="Tasks" value={tasks.length} />
            <Typography.Text type="secondary">
              {tasks.filter((task) => task.completedAt).length} completed
            </Typography.Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Subtasks" value={subtasks.length} />
            <Typography.Text type="secondary">
              {completedSubtasks} completed · {pendingSubtasks} pending
            </Typography.Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Metrics" value={metrics.length} />
            <Typography.Text type="secondary">
              {metrics.filter((metric) => metric.completedAt).length} completed
            </Typography.Text>
          </Card>
        </Col>
      </Row>
      <Card title="Upcoming (Next 14 Days)">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {upcomingMetrics.length === 0 && upcomingSubtasks.length === 0 ? (
            <Typography.Text type="secondary">
              No upcoming metrics or subtasks.
            </Typography.Text>
          ) : (
            <>
              {upcomingMetrics.length > 0 && (
                <div>
                  <Typography.Text strong>Metrics</Typography.Text>
                  <List
                    aria-label="Upcoming metrics in next 14 days"
                    dataSource={upcomingMetrics}
                    renderItem={(metric) => (
                      <List.Item>
                        <Space direction="vertical" size={0}>
                          <Typography.Text>{metric.title}</Typography.Text>
                          <Typography.Text type="secondary">
                            Due{" "}
                            {metric.dueDate
                              ? new Date(metric.dueDate).toLocaleDateString()
                              : "—"}
                          </Typography.Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              )}
              {upcomingSubtasks.length > 0 && (
                <div>
                  <Typography.Text strong>Subtasks</Typography.Text>
                  <List
                    aria-label="Upcoming subtasks in next 14 days"
                    dataSource={upcomingSubtasks}
                    renderItem={(subtask) => (
                      <List.Item>
                        <Space direction="vertical" size={0}>
                          <Typography.Text>{subtask.title}</Typography.Text>
                          <Typography.Text type="secondary">
                            Due{" "}
                            {subtask.dueDate
                              ? new Date(subtask.dueDate).toLocaleDateString()
                              : "—"}
                          </Typography.Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </>
          )}
        </Space>
      </Card>
    </Space>
  );
}
